"use client";

// BrandAvatar — a real app-icon for an AI tool: a rounded-rect tile in the
// brand's own color with its logo centered, NO inner disc / circle mask / frame.
// It mirrors @lobehub/icons' own Avatar treatment (white/dark mono logo on the
// brand color, or the multicolor logo on white) but WITHOUT pulling in the
// package's Avatar → @lobehub/ui → antd chain: we deep-import only the
// dependency-free Mono/Color SVG components + read each brand's real color here.
//
// Brands @lobehub/icons doesn't carry yet (zed, continue) fall back to our own
// BrandIcon mark on a hand-set brand tile, so the set stays consistent.

import type { ComponentType, CSSProperties } from "react";
import BrandIcon, { brandKey } from "./BrandIcon";

// dependency-free deep imports (the brand index pulls Avatar → @lobehub/ui/antd).
import ClaudeMono from "@lobehub/icons/es/Claude/components/Mono";
import ClaudeCodeMono from "@lobehub/icons/es/ClaudeCode/components/Mono";
import GithubCopilotMono from "@lobehub/icons/es/GithubCopilot/components/Mono";
import OpenAIMono from "@lobehub/icons/es/OpenAI/components/Mono";
import CursorMono from "@lobehub/icons/es/Cursor/components/Mono";
import ClineMono from "@lobehub/icons/es/Cline/components/Mono";
import WindsurfMono from "@lobehub/icons/es/Windsurf/components/Mono";
import AntigravityColor from "@lobehub/icons/es/Antigravity/components/Color";
import GeminiColor from "@lobehub/icons/es/Gemini/components/Color";
import GoogleColor from "@lobehub/icons/es/Google/components/Color";
import DeepSeekMono from "@lobehub/icons/es/DeepSeek/components/Mono";
import PerplexityMono from "@lobehub/icons/es/Perplexity/components/Mono";
import ObsidianMono from "@lobehub/icons/es/Obsidian/components/Mono";
import NotionMono from "@lobehub/icons/es/Notion/components/Mono";
import OllamaMono from "@lobehub/icons/es/Ollama/components/Mono";
import GithubMono from "@lobehub/icons/es/Github/components/Mono";

type LobeIcon = ComponentType<{ size?: number | string; color?: string; style?: CSSProperties }>;

interface Spec {
  Icon: LobeIcon;
  /** the tile ground — the brand's own color. */
  bg: string;
  /** the logo color (mono logos honor it; multicolor logos ignore it). */
  color: string;
  /** logo size as a fraction of the tile. */
  scale: number;
}

// Colors + mono/color choice taken straight from each brand's @lobehub/icons
// avatar spec (AVATAR_BACKGROUND / AVATAR_COLOR), so these are the "right colors".
const SPECS: Record<string, Spec> = {
  claude:      { Icon: ClaudeMono,       bg: "#D97757", color: "#fff", scale: 0.6 },
  // Claude's family wears Claude's ground with the mark in white (John, 2026-10-15); Claude Code is the boxy one
  "claude-code": { Icon: ClaudeCodeMono, bg: "#D97757", color: "#fff", scale: 0.66 },
  copilot:     { Icon: GithubCopilotMono, bg: "#000000", color: "#fff", scale: 0.62 },
  openai:      { Icon: OpenAIMono,       bg: "#000000", color: "#fff", scale: 0.62 },
  cursor:      { Icon: CursorMono,       bg: "#000000", color: "#fff", scale: 0.58 },
  cline:       { Icon: ClineMono,        bg: "#323B43", color: "#fff", scale: 0.62 },
  windsurf:    { Icon: WindsurfMono,     bg: "#0A0A0A", color: "#fff", scale: 0.6 },
  antigravity: { Icon: AntigravityColor, bg: "#ffffff", color: "#fff", scale: 0.64 },
  gemini:      { Icon: GeminiColor,      bg: "#ffffff", color: "#fff", scale: 0.66 },
  google:      { Icon: GoogleColor,      bg: "#ffffff", color: "#fff", scale: 0.6 },
  deepseek:    { Icon: DeepSeekMono,     bg: "#4D6BFE", color: "#fff", scale: 0.62 },
  perplexity:  { Icon: PerplexityMono,   bg: "#22B8CD", color: "#fff", scale: 0.62 },
  obsidian:    { Icon: ObsidianMono,     bg: "#7C3AED", color: "#fff", scale: 0.62 },
  notion:      { Icon: NotionMono,       bg: "#ffffff", color: "#111111", scale: 0.62 },
  ollama:      { Icon: OllamaMono,       bg: "#0A0A0A", color: "#fff", scale: 0.62 },
  github:      { Icon: GithubMono,       bg: "#0A0A0A", color: "#fff", scale: 0.6 },
};

// brands @lobehub/icons doesn't carry yet — our own mark, on a hand-set tile. Codex is
// here too: the pack's mark is the old one, and Codex wears its own app mark everywhere
// (BrandIcon, 2026-10-29), filling its white tile as the real icon does.
const FALLBACK: Record<string, { bg: string; color?: string; scale?: number }> = {
  zed:      { bg: "#ffffff" },            // our zed mark carries its own blue fill
  continue: { bg: "#0A0A0A", color: "#fff" },
  codex:    { bg: "#ffffff", scale: 1 },
  // the new rows of 2026-11-03: grok wears its app's black ground; le chat its orange m on
  // white (on the organism's tile it read orange on yellow); openclaw its red lobster on night
  grok:     { bg: "#000000", color: "#fff", scale: 0.66 },
  "grok-bot": { bg: "#0A0A0A", color: "#fff", scale: 0.58 },
  mistral:  { bg: "#ffffff", scale: 0.58 },
  openclaw: { bg: "#0B1020", scale: 0.72 },
  hermes:   { bg: "#ffffff", color: "#000", scale: 0.66 },
};

/** does this brand have an app tile of its own (its ground and its mark), rather than a bare mark? */
export function hasBrandTile(name: string | null | undefined): boolean {
  const key = (brandKey(name) ?? name ?? "").toString();
  return !!SPECS[key] || !!FALLBACK[key];
}

/** true where the tile ground is light enough to need a hairline to read on cream. */
const isLight = (hex: string) => {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 210;
};

export default function BrandAvatar({
  name,
  size = 48,
  radius,
  className,
  style,
}: {
  name: string | null | undefined;
  size?: number;
  /** override the corner radius (default ~24% — an app-icon squircle). */
  radius?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const key = (brandKey(name) ?? name ?? "").toString();
  const spec = SPECS[key];
  const fb = FALLBACK[key];
  const r = radius ?? Math.round(size * 0.24);
  const bg = spec?.bg ?? fb?.bg ?? "var(--organism-color, #2f7fdb)";
  const tileStyle: CSSProperties = {
    width: size, height: size, borderRadius: r, background: bg,
    ...(isLight(spec?.bg ?? fb?.bg ?? "#000") ? { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" } : {}),
    ...style,
  };

  return (
    <span className={`brand-avatar${className ? " " + className : ""}`} style={tileStyle} aria-hidden>
      {spec ? (
        <spec.Icon size={Math.round(size * spec.scale)} color={spec.color} />
      ) : (
        <span className="brand-avatar-mark" style={{ width: Math.round(size * (fb?.scale ?? 0.6)), height: Math.round(size * (fb?.scale ?? 0.6)), color: fb?.color ?? "var(--ink)" }}>
          <BrandIcon name={name} size={Math.round(size * (fb?.scale ?? 0.6))} />
        </span>
      )}
    </span>
  );
}
